import { IComponentOptions, IQService } from "angular";
import { IEnrichedTeam, ITeam, ITeamsStoreState, TeamsActions, TeamService } from "@gtmhub/teams";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { ITeamEventArgs, TeamEvents } from "@gtmhub/teams/events";
import { getToken } from "@gtmhub/auth";
import { INgRedux } from "@gtmhub/state-management";
import { GtmhubController } from "@gtmhub/core";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { IModalComponent } from "@gtmhub/core/routing";
import { StateService } from "@uirouter/angularjs";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";
import { StatusChangePayload } from "@webapp/teams/models/status-actions.models";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { ReflectionsFacade } from "@webapp/reflections/services/reflections-facade.service";
import { IReflection } from "@webapp/reflections/models/reflections.models";
import { take } from "rxjs";

const teamProfileActiveTab = ["details", "reflections"] as const;
type TeamProfileActveTab = typeof teamProfileActiveTab[number];
const DEFAULT_ACTIVE_TAB = "details";

interface ITeamProfileStateParamsService {
  teamId: string;
  focusSelectManagerField?: boolean;
  activeTab: TeamProfileActveTab;
  cadenceStart: string;
  memberId: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ITeamProfileBindings extends IModalComponent {}

const bindStateToCtrl = (state: ITeamsStoreState & IAssigneesStoreState) => {
  const { map: teams, isFetched: teamsFetched, error: teamsError } = state.teams;
  const { map: assignees, isFetched: assigneesFetched, error: assigneesError } = state.assignees;

  return {
    teams,
    assignees,
    reduxItemsFetched: teamsFetched && assigneesFetched,
    reduxItemsError: teamsError || assigneesError,
  };
};

export interface TeamProfileCtrl extends ITeamProfileBindings, ReturnType<typeof bindStateToCtrl> {}
export class TeamProfileCtrl extends GtmhubController {
  private teamEvents: TeamEvents;
  team: IEnrichedTeam;
  token: string;
  indicators: {
    loading: boolean;
    loadingReflection: boolean;
  };
  isReflectionsFeatureEnabled: boolean;
  activeTabIndex: { [activeTab in TeamProfileActveTab]: number };
  public reflection: IReflection;
  public activeTab: TeamProfileActveTab;
  public focusSelectManagerField: boolean;

  showNameEditor = false;

  private checkingEnabledFeatures: boolean;
  private showNewExperience: boolean;
  public favoritesManager: FavoritesManager;

  static $inject = [
    "$scope",
    "$ngRedux",
    "TeamsActions",
    "AssigneeActions",
    "$stateParams",
    "TeamService",
    "UIErrorHandlingService",
    "$state",
    "FeatureToggleService",
    "FavoritesManagerFactory",
    "AccountService",
    "$q",
    "ReflectionsFacade",
  ];

  constructor(
    private $scope: IExtendedRootScopeService,
    $ngRedux: INgRedux,
    teamsActions: TeamsActions,
    assigneeActions: AssigneeActions,
    private $stateParams: ITeamProfileStateParamsService,
    private teamService: TeamService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $state: StateService,
    private featureToggleService: FeatureToggleService,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private accountService: AccountService,
    private $q: IQService,
    private reflectionsFacade: ReflectionsFacade
  ) {
    super();
    this.focusSelectManagerField = !!this.$stateParams.focusSelectManagerField;
    this.onDestroy($ngRedux.connect(bindStateToCtrl)(this));
    $ngRedux.dispatch(teamsActions.fetchTeamsIfMissing());
    $ngRedux.dispatch(assigneeActions.getAssignees());

    this.teamEvents = new TeamEvents(this.$scope);
    this.favoritesManager = this.favoritesManagerFactory(["user", "team"]);
  }

  $onInit(): void {
    this.indicators = {
      loading: true,
      loadingReflection: true,
    };

    this.teamEvents.onTeamUpdated((args) => this.onTeamUpdated(args));

    this.token = getToken();

    this.checkingEnabledFeatures = true;
    this.showNewExperience = false;

    this.$scope
      .watchUntilAndRejectOnError(
        () => this.reduxItemsFetched,
        () => this.reduxItemsError
      )
      .then(() => {
        this.$q
          .all({
            newExperienceEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NewExperience),
            newExperienceToggleEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NewExperienceToggle),
            reflectionsEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.FeatureReflections),
            reflectionsModuleEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.ModuleReflections),
            teamsCollection: this.teamService.getTeams({ filter: { _id: this.$stateParams.teamId }, fields: "id,parentId,badges" }),
          })
          .then(({ newExperienceEnabled, newExperienceToggleEnabled, reflectionsEnabled, reflectionsModuleEnabled, teamsCollection }) => {
            // there always be only one element in the items
            const team = teamsCollection.items[0];
            this.team = { ...this.teams[this.$stateParams.teamId], parentId: team.parentId, badges: team.badges, type: "team" };

            this.teamEvents.broadcastTeamVisited(this.team);

            this.isReflectionsFeatureEnabled = reflectionsEnabled && reflectionsModuleEnabled;
            if (this.isReflectionsFeatureEnabled) {
              this.loadTeamReflection();
            } else {
              this.indicators.loading = false;
              this.indicators.loadingReflection = false;
            }

            const newExperienceAccountSettingEnabled = this.accountService.getAccountSetting<Record<"enabled", boolean>>("newExperience")?.enabled;
            this.showNewExperience = newExperienceEnabled && newExperienceToggleEnabled && newExperienceAccountSettingEnabled;
            this.checkingEnabledFeatures = false;
            if (this.showNewExperience) {
              this.favoritesManager.initFavorites();
            }
          });
      })
      .catch((error) => {
        this.checkingEnabledFeatures = false;
        this.indicators.loading = false;
        this.uiErrorHandlingService.handleModal(error);
      });
  }

  public updateField(field: keyof ITeam, value: string | string[]): void {
    const team: Partial<ITeam> = {
      id: this.team.id,
      [field]: value,
    };

    this.teamService.updateTeam(team).then(
      (updated) => {
        this.team = Object.assign(this.team, updated);
        this.teamEvents.broadcastTeamUpdated({ team: this.team });
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  }

  public openAvatarSelector = (): void => {
    this.$state.go("._form.form", { teamId: this.team.id, "avatar-selector-only": true });
  };

  public handleTeamStatusChange(payload: StatusChangePayload): void {
    this.broadcastTeamUpdated(payload);
    this.returnToState();
  }

  public handleTeamDeleted(): void {
    this.broadcastTeamDeleted();
    this.returnToState();
  }

  public broadcastTeamUpdated(payload: StatusChangePayload): void {
    if (!payload.hasFailures) {
      this.teamEvents.broadcastTeamUpdated({ team: { ...this.team, isActive: payload.action === "activate" } });
    }
  }

  public broadcastTeamDeleted(): void {
    this.teamEvents.broadcastTeamDeleted({ teamId: this.team.id, parentId: this.team.parentId });
  }

  public returnToState(): void {
    this.$state.go("gtmhub.teams");
  }

  private onTeamUpdated = (args: ITeamEventArgs): void => {
    this.team = { ...this.team, ...args.team, type: "team" };
  };

  public getNavItem = (): INavItem => {
    if (!this.team) return undefined;
    return this.favoritesManager.getFavoriteItem({ id: this.team.id }) || navItemsFactory.buildTeamNavItem(this.team);
  };

  public teamIsFavorite = (): boolean => {
    if (!this.team) return false;
    if (!this.isNewExperienceEnabled()) return false;
    return this.favoritesManager.entityIsFavorite({ id: this.team.id });
  };

  public isNewExperienceEnabled = (): boolean => {
    return !this.checkingEnabledFeatures && this.showNewExperience;
  };

  public changeActiveTab = (activeTab: "details" | "reflections", params: { replaceLocation: boolean } = { replaceLocation: true }): void => {
    if (activeTab === this.activeTab) return;

    this.indicators.loadingReflection = true;
    this.activeTab = activeTab;
    this.$state
      .go(
        ".",
        { ...this.$stateParams, activeTab, memberId: null, cadenceStart: null },
        { reload: false, notify: false, location: params.replaceLocation ? "replace" : true }
      )
      .finally(() => {
        this.indicators.loadingReflection = false;
      });
  };

  private loadTeamReflection(): void {
    this.reflectionsFacade
      .getTeamReflection$({ teamId: this.team.id })
      .pipe(take(1))
      .subscribe({
        next: (reflection) => {
          if (!reflection) return;

          this.reflection = reflection;
          this.activeTabIndex = {
            details: 0,
            reflections: 1,
          };
          const isActiveTabValid = this.$stateParams.activeTab && teamProfileActiveTab.includes(this.$stateParams.activeTab);
          if (isActiveTabValid) {
            this.activeTab = this.$stateParams.activeTab;
            this.indicators.loadingReflection = false;
          } else {
            this.changeActiveTab(DEFAULT_ACTIVE_TAB, { replaceLocation: true });
          }

          this.indicators.loading = false;
        },
        error: () => {
          this.indicators.loading = false;
          this.indicators.loadingReflection = false;
        },
      });
  }
}

export const TeamProfileComponent: IComponentOptions = {
  controller: TeamProfileCtrl,
  bindings: {
    dismiss: "&",
    close: "&",
  },
  templateUrl: require("./team-profile.component.jade"),
};
